import { useEffect, useState } from 'react';
import { openTickerChart } from '../tickerChart';
import { OrderTicket } from './OrderTicket';

interface DiscoverStock {
  ticker: string;
  name: string | null;
  price: number | null;
  changePercent: number | null;
  mentions: number | null;
  mentionChange24h: number | null;
  dollarVolume: number | null;
}

/** "$1.2B" / "$519M" compact traded-value label */
function compactDollars(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  return `$${(n / 1e3).toFixed(0)}K`;
}

interface DiscoverData {
  updatedAt: string;
  hot: DiscoverStock[];
  trending: DiscoverStock[];
  rising: DiscoverStock[];
}

type Tab = 'hot' | 'trending' | 'rising';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'hot', label: '🔥 HOT' },
  { key: 'trending', label: '💬 TRENDING' },
  { key: 'rising', label: '🚀 RISING' },
];

// Green for gains, red for losses, intensity saturating at ±3%
function cellColor(changePercent: number | null): string {
  if (changePercent === null) return 'rgba(120, 140, 125, 0.9)';
  const t = Math.min(Math.abs(changePercent) / 3, 1);
  return changePercent >= 0
    ? `rgba(0, 255, 65, ${0.35 + 0.65 * t})`
    : `rgba(255, 59, 59, ${0.35 + 0.65 * t})`;
}

/**
 * Line + area paths for a 100×100 viewBox, occupying the lower band of the
 * tile (y 48–96) so the chart reads as a backdrop under the tile text.
 */
function sparkPaths(closes: number[]): { line: string; area: string } {
  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const span = max - min || 1;
  const pts = closes.map((c, i) => {
    const x = (i / (closes.length - 1)) * 100;
    const y = 96 - ((c - min) / span) * 48;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const line = `M${pts.join(' L')}`;
  const area = `${line} L100,100 L0,100 Z`;
  return { line, area };
}

export function Discover() {
  const [data, setData] = useState<DiscoverData | null>(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState<Tab>('hot');
  const [sparks, setSparks] = useState<Record<string, number[]>>({});
  const [armed, setArmed] = useState(false); // order buttons only when local execution is enabled
  const [ticket, setTicket] = useState<{ ticker: string; price: number | null } | null>(null);

  useEffect(() => {
    fetch('/api/orders/armed')
      .then((r) => (r.ok ? r.json() : { armed: false }))
      .then((d) => setArmed(Boolean(d.armed)))
      .catch(() => setArmed(false));
  }, []);

  useEffect(() => {
    const load = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch('/api/discover', { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        setData(await response.json());
        setError(false);
      } catch (e) {
        console.error('Error fetching discover:', e);
        setError(true);
      } finally {
        clearTimeout(timeout);
      }
    };
    load();
    const interval = setInterval(load, 300000);
    return () => clearInterval(interval);
  }, []);

  // 4h-bar sparklines: the backend fills its cache through a paced worker,
  // so poll every 20s until every visible tile has its series
  useEffect(() => {
    if (!data) return;
    let stopped = false;

    const loadSparks = async () => {
      const tickers = data[tab].map((s) => s.ticker).filter((t) => !(t in sparks));
      if (tickers.length === 0) return;
      try {
        const response = await fetch(`/api/sparks?tickers=${tickers.join(',')}`);
        if (!response.ok) return;
        const payload = await response.json();
        if (!stopped && payload.sparks && Object.keys(payload.sparks).length > 0) {
          setSparks((prev) => ({ ...prev, ...payload.sparks }));
        }
      } catch {
        /* transient — next poll retries */
      }
    };

    loadSparks();
    const interval = setInterval(loadSparks, 20000);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [data, tab, sparks]);

  if (error && !data) return <div className="empty-state">Discovery unavailable</div>;
  if (!data) return <div className="loading">Loading...</div>;

  const stocks = data[tab];

  return (
    <div className="discover">
      <div className="disc-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`disc-tab${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {stocks.length === 0 ? (
        <div className="empty-state">Nothing here right now</div>
      ) : (
        <div className="heatmap-grid disc-grid">
          {stocks.map((s) => {
            const color = cellColor(s.changePercent);
            const spark = sparks[s.ticker];
            return (
              <div
                key={s.ticker}
                className="heatmap-cell"
                onClick={() => openTickerChart(s.ticker)}
                style={{
                  borderColor: color,
                  backgroundColor: color.replace(/[\d.]+\)$/, '0.12)'),
                  color,
                  cursor: 'pointer',
                }}
                title={`${s.name || s.ticker} — click for chart`}
              >
                {spark && spark.length > 3 && (
                  <svg className="cell-spark" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id={`sg-${s.ticker}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.35" />
                        <stop offset="100%" stopColor={color} stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d={sparkPaths(spark).area} fill={`url(#sg-${s.ticker})`} />
                    <path
                      d={sparkPaths(spark).line}
                      fill="none"
                      stroke={color}
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                )}
                {armed && (
                  <button
                    className="buy-btn cell-buy"
                    title={`Buy ${s.ticker}`}
                    onClick={(e) => {
                      e.stopPropagation(); // don't also open the chart
                      setTicket({ ticker: s.ticker, price: s.price });
                    }}
                  >
                    BUY
                  </button>
                )}
                <div className="cell-ticker">{s.ticker}</div>
                <div className="cell-score">
                  {s.changePercent !== null
                    ? `${s.changePercent >= 0 ? '+' : ''}${s.changePercent.toFixed(1)}%`
                    : '—'}
                </div>
                <div className="cell-price">{s.price !== null ? `$${s.price.toFixed(2)}` : '—'}</div>
                {tab === 'hot' && s.dollarVolume !== null && (
                  <div className="cell-sub">{compactDollars(s.dollarVolume)} traded</div>
                )}
                {tab === 'trending' && s.mentions !== null && (
                  <div className="cell-sub">💬 {s.mentions}</div>
                )}
                {tab === 'rising' && s.mentionChange24h !== null && (
                  <div className="cell-sub">💬 +{Math.round(s.mentionChange24h)}%</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {ticket && (
        <OrderTicket
          ticker={ticket.ticker}
          action="BUY"
          maxQty={1}
          mark={ticket.price}
          onClose={() => setTicket(null)}
        />
      )}

      <div className="legend">
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: 'rgba(255,59,59,1)' }}></span>
          <span>≤ −3%</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: 'rgba(120,140,125,0.6)' }}></span>
          <span>0</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: 'rgba(0,255,65,1)' }}></span>
          <span>≥ +3%</span>
        </div>
      </div>
    </div>
  );
}
